import { googleLoginUrl } from "../auth/googleLoginUrl";
import { TermsNotice, rememberTermsNoticeSeen } from "../auth/TermsNotice";
import { TERMS_VERSION, TERMS_VERSION_PARAM } from "@/app/utils/legal";
import {
  Form,
  useNavigate,
  useIntendedUrl,
  useDictionary,
  ValidationErrors,
} from "gemi/client";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { type AuthIntent, setAuthIntent, clearAuthIntent } from "./authIntent";
import { useLandingSession } from "./useLandingSession";
import { font } from "./_shared";
import { authModalV2Dict } from "./AuthModalV2.i18n";

type AuthMode = "signin" | "signup";
type Step = "method" | "email" | "pin";

type AuthModalContextValue = { open: (mode: AuthMode) => void };

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

/** Opens the v2 auth modal. Available anywhere under <AuthModalProvider>. */
export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error("useAuthModal must be used within an AuthModalProvider");
  }
  return ctx;
}

/**
 * Single entry point for every v2 landing CTA: stash the intent, then either open the sign-up modal
 * (anonymous visitor) or, for an already-signed-in visitor, return to the requested page, falling
 * back to /app/redirect to resolve their org. Keeps a logged-in user off the sign-up
 * screen and keeps the intent-then-auth logic in one place.
 *
 * `/v2` is public-cached, so the session read legitimately starts as `loading` with no user. A click that
 * lands in that window must NOT be treated as signed-out: the intent and routing are
 * DEFERRED until `loading` clears, then a real user takes the shortcut and only a confirmed
 * signed-out visitor sees sign-up.
 */
export function useV2AuthEntry() {
  const { open } = useAuthModal();
  const intended = useIntendedUrl("/app/redirect");
  const { user, loading } = useLandingSession();
  const [pending, setPending] = useState<AuthIntent | null>(null);

  const route = useCallback(
    (intent: AuthIntent) => {
      setAuthIntent(intent, user?.publicId ?? null);
      if (user) {
        window.location.href = intended;
      } else {
        open("signup");
      }
    },
    [user, open, intended],
  );

  useEffect(() => {
    if (pending && !loading) {
      setPending(null);
      route(pending);
    }
  }, [pending, loading, route]);

  return useCallback(
    (intent: AuthIntent) => {
      if (loading) {
        setPending(intent);
        return;
      }
      route(intent);
    },
    [loading, route],
  );
}

/**
 * v2-local login/sign-up modal. Kept self-contained (no legacy AuthDialog import)
 * so the /v2 tree stays free of the legacy landing views. Drives the same working
 * magic-link + PIN endpoints the customer auth page uses, but renders once inside
 * the landing layout instead of navigating to the (separately mounted) /auth pages.
 */
export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<AuthMode>("signin");
  const [isOpen, setIsOpen] = useState(false);

  // A return to the landing (including OAuth Back) abandons the previous hand-off.
  useEffect(() => {
    clearAuthIntent();
  }, []);
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        clearAuthIntent();
        setIsOpen(false);
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const open = useCallback((next: AuthMode) => {
    setMode(next);
    setIsOpen(true);
  }, []);

  return (
    <AuthModalContext.Provider value={{ open }}>
      {children}
      {isOpen && (
        <AuthModal
          mode={mode}
          onModeChange={setMode}
          onClose={() => {
            clearAuthIntent();
            setIsOpen(false);
          }}
        />
      )}
    </AuthModalContext.Provider>
  );
}

const inputClass =
  "w-full rounded-full border border-[rgba(25,24,24,0.16)] bg-white px-4 py-3 text-[15px] text-[#343139] outline-none placeholder:text-[#9b99a1] focus:border-[#f0b04d]";
const darkBtn =
  "flex w-full items-center justify-center gap-2 rounded-full bg-[#19180F] px-6 py-3.5 text-[15px] font-medium text-[#F7F7F2] no-underline disabled:opacity-60";
const noticeClass = "m-0 text-center text-[12px] leading-[1.5] text-[#86848A]";
const ghostBtn =
  "flex w-full items-center justify-center gap-2 rounded-full border border-[rgba(25,24,24,0.16)] bg-white px-6 py-3.5 text-[15px] font-medium text-[#343139]";

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

function AuthModal({
  mode,
  onModeChange,
  onClose,
}: {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  onClose: () => void;
}) {
  const t = useDictionary(authModalV2Dict);
  const { replace } = useNavigate();
  const intended = useIntendedUrl("/app/redirect");
  const [step, setStep] = useState<Step>("method");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const isSignup = mode === "signup";
  const title = isSignup ? t("sign-up") : t("sign-in");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-5"
      style={font.body}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(25,24,24,0.55)]"
      />
      <div className="relative z-10 flex w-full max-w-[420px] flex-col gap-6 rounded-[24px] border border-[rgba(25,24,24,0.1)] bg-white p-8 shadow-[0_24px_64px_-24px_rgba(25,24,24,0.45)]">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute right-5 top-5 text-[20px] leading-none text-[#86848A]"
        >
          ×
        </button>

        <div className="flex flex-col gap-1">
          <h2
            className="m-0 text-[26px] font-medium tracking-[-0.5px] text-[#343139]"
            style={font.display}
          >
            {title}
          </h2>
          <p className="m-0 text-[14px] text-[#86848A]">
            {step === "pin"
              ? t("pin-sent-to-email", { email })
              : isSignup
                ? t("get-started")
                : t("access-your-account")}
          </p>
        </div>

        {step === "method" && (
          <div className="flex flex-col gap-3">
            {/* A first Google sign-in creates the account too, so the notice shows in both modes. */}
            <a
              href={googleLoginUrl(intended)}
              onClick={rememberTermsNoticeSeen}
              className={ghostBtn}
            >
              <GoogleGlyph />
              {isSignup ? t("sign-up-with-google") : t("continue-with-google")}
            </a>
            <button
              type="button"
              onClick={() => setStep("email")}
              className={darkBtn}
            >
              {isSignup ? t("sign-up-with-email") : t("sign-in-with-email")}
            </button>
            <TermsNotice className={noticeClass} />
          </div>
        )}

        {step === "email" && (
          <Form
            method="POST"
            // Sign up CREATES the account (name + email) and sends the PIN; sign in
            // sends a PIN only to an existing account. Using magic-link for a new
            // email would 200 without creating anything and dead-end at the PIN step.
            action={isSignup ? "/auth/sign-up" : "/auth/magic-link"}
            search={
              isSignup ? { [TERMS_VERSION_PARAM]: TERMS_VERSION } : undefined
            }
            onSuccess={({ email }) => {
              if (!email) return;
              setEmail(email);
              setStep("pin");
            }}
            className="flex flex-col gap-4"
          >
            {isSignup && (
              <div className="flex flex-col gap-2">
                <input
                  name="name"
                  type="text"
                  required
                  placeholder={t("enter-your-name")}
                  className={inputClass}
                  style={font.body}
                />
                <ValidationErrors
                  name="name"
                  className="text-[13px] text-red-500"
                />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <input
                name="email"
                type="email"
                required
                placeholder={t("enter-your-email")}
                className={inputClass}
                style={font.body}
              />
              <ValidationErrors
                name="email"
                className="text-[13px] text-red-500"
              />
              <ValidationErrors
                name="invalid_email"
                className="text-[13px] text-red-500"
              />
              <ValidationErrors
                name="general"
                className="text-[13px] text-red-500"
              />
            </div>
            <button type="submit" className={darkBtn}>
              {isSignup ? t("create-account") : t("send-pin-code")}
            </button>
            {isSignup && <TermsNotice className={noticeClass} />}
            <button
              type="button"
              onClick={() => setStep("method")}
              className="text-[14px] text-[#86848A] hover:text-[#343139]"
            >
              {t("back")}
            </button>
          </Form>
        )}

        {step === "pin" && (
          <Form
            method="POST"
            action="/auth/sign-in-with-pin-v2"
            onSuccess={() => {
              replace(intended);
            }}
            className="flex flex-col gap-4"
          >
            <input type="hidden" name="email" value={email} />
            <div className="flex flex-col gap-2">
              <input
                name="pin"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
                placeholder={t("pin-code")}
                className={`${inputClass} text-center tracking-[0.4em]`}
                style={font.body}
              />
              <ValidationErrors
                name="pin"
                className="text-[13px] text-red-500"
              />
            </div>
            <button type="submit" className={darkBtn}>
              {t("verify-pin")}
            </button>
            <button
              type="button"
              onClick={() => setStep("email")}
              className="text-[14px] text-[#86848A] hover:text-[#343139]"
            >
              {t("back-to-email")}
            </button>
          </Form>
        )}

        {step === "method" && (
          <p className="m-0 text-center text-[14px] text-[#86848A]">
            {isSignup ? t("already-have-account") : t("dont-have-account")}{" "}
            <button
              type="button"
              onClick={() => onModeChange(isSignup ? "signin" : "signup")}
              className="font-medium text-[#343139] hover:underline"
            >
              {isSignup ? t("sign-in") : t("sign-up")}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
